import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { usePermissions } from '@/api/permissions';
import { activeMobileTab, mobileTabsFor } from '@/lib/permissions';
import { moduleForPath } from '@/lib/navigation';
import { MODULES } from '@/lib/modules';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { MoreSheet } from '@/components/shell/MoreSheet';

/**
 * Phone tab bar (Mobile.md §3): up to four task tabs chosen for the user's role (`mobileTabsFor`, from
 * `MOBILE_TABS` in navigation.ts) plus **More**, a sheet with every module the role can open, search, the client
 * picker and settings. A page no tab owns lights More, which then shows its module's icon and name.
 */
export function BottomNav() {
  const { pathname } = useLocation();
  const perms = usePermissions();
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => { setMoreOpen(false); }, [pathname]);
  const tabs = mobileTabsFor(perms);
  const active = activeMobileTab(pathname, tabs);
  const mod = !active && pathname !== ROUTES.home ? MODULES.find((m) => m.id === moduleForPath(pathname)) : undefined;
  const MoreIcon = mod?.icon ?? Menu;

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {tabs.map((t) => <Tab key={t.id} to={t.to} label={t.label} icon={t.icon} active={active?.id === t.id} />)}
        <button type="button" onClick={() => setMoreOpen(true)} aria-label="More"
          className={cn('group flex flex-1 flex-col items-center gap-1 px-1 pb-2 pt-1.5 text-[10.5px] font-medium transition-colors',
            !active && pathname !== ROUTES.home ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}>
          <TabIcon icon={MoreIcon} active={!active && pathname !== ROUTES.home} />
          <span className="max-w-[4.5rem] truncate tracking-[0.01em]">{mod?.label ?? 'More'}</span>
        </button>
      </nav>
      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
}

function TabIcon({ icon: Icon, active }: { icon: React.ComponentType<{ className?: string }>; active: boolean }) {
  return (
    <span className={cn('inline-flex h-7 w-12 items-center justify-center rounded-full transition-colors',
      active ? 'bg-brand-soft text-brand-strong dark:text-brand' : 'group-hover:bg-muted')}>
      <Icon className={cn('h-[18px] w-[18px]', active && 'stroke-[2.25]')} />
    </span>
  );
}

function Tab({ to, label, icon, active }: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; active: boolean }) {
  return (
    <Link to={to}
      className={cn('group flex flex-1 flex-col items-center gap-1 px-1 pb-2 pt-1.5 text-[10.5px] font-medium transition-colors',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}>
      <TabIcon icon={icon} active={active} />
      <span className="tracking-[0.01em]">{label}</span>
    </Link>
  );
}
